import { useEffect, useState } from 'react';
import { Modal } from '../_common';
import styled from '@emotion/styled';
import PolarisIcon from '../_common/PolarisIcon';
import axios from '@/_utils/axios';
import { useAppSelector } from '@/redux/hooks';
import { selectCurrentPath, selectHomePathId } from '@/redux/selectors/fileManagerSelectors';
import { selectUserEmail } from '@/redux/selectors/authSelectors';
import { IngestFile } from '@/redux/slices/slice_types';

interface SigniantUploadProps {
  open: boolean;
  file?: IngestFile;
  onClose: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

type SigniantUploadBody = {
  input_data: { user_email: string; directory?: string; receive_upload_notification?: boolean };
  metadata?: { ingest_record_id: number };
};

const SigniantUpload = ({ open, onClose, file }: SigniantUploadProps) => {
  const currentPathId = useAppSelector(selectHomePathId);
  const currentPath = useAppSelector(selectCurrentPath);
  const [iframeUrl, setIframeUrl] = useState<string>('');
  const [iframeError, setIframeError] = useState<boolean>(false);
  const userEmail = useAppSelector(selectUserEmail) || 'tmtuser@tmtinsights.com';
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!iframeUrl && open) {
      generateIframeUrl();
    } else if (!open) {
      setIframeUrl('');
    }
  }, [open]);

  useEffect(() => {
    if (iframeUrl) {
      generateIframeUrl();
    }
  }, [open]);

  const generateIframeUrl = () => {
    setLoading(true);
    const directory = currentPath.slice(1).join('/');

    const body: SigniantUploadBody = {
      input_data: { user_email: userEmail }
    };

    if (!file) {
      body.input_data.directory = directory;
    } else {
      body.input_data.receive_upload_notification = true;
      body.metadata = { ingest_record_id: file.id };
    }

    axios
      .post(`vfs/file_sources/${file && file.upload_file_origin_id ? file.upload_file_origin_id : currentPathId}/upload_info/signiant/`, body)
      .then((response) => {
        setIframeUrl(response.data?.data?.url);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleIframeError = () => {
    setIframeError(true);
  };

  return (
    <StyledModal loading={loading} open={open} title="Signiant Upload" onClose={onClose} icon={<PolarisIcon icon="cloud_upload" />}>
      {iframeError ? (
        <div>
          <p>
            Unable to load the upload page due to Content Security Policy restrictions. Please{' '}
            <a href={iframeUrl} target="_blank" rel="noopener noreferrer">
              click here
            </a>
            to open it in a new tab.
          </p>
        </div>
      ) : (
        <div>
          <iframe src={iframeUrl} width="100%" height="550px" onError={handleIframeError} />
        </div>
      )}
    </StyledModal>
  );
};

export default SigniantUpload;

const StyledModal = styled(Modal)`
  & > .MuiPaper-root {
    max-width: 800px;
  }

  iframe {
    border: 1px solid ${({ theme }) => theme.palette.divider};
  }
`;
